import React, { useState, useEffect, useContext, useMemo } from "react";
import { AuthContext } from "../../../contexts/AuthContext";
import Header from "../../../layout/Header/Header";
import {
  Button,
  Row,
  Table,
  Input,
  Label,
  ButtonGroup,
  Col,
  Badge,
} from "reactstrap";
import Swal from "sweetalert2";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import ExportJsonExcel from "js-export-excel";
import axios from "axios";
import moment from "moment";

// (2026-09) Reporte de costos de corte con ficha por corte: resumen arriba y,
// por cada corte, el desglose de telas, procesos y habilitación. Mismos datos
// en pantalla, PDF y Excel.
function ReporteCostosCortes() {
  const { user } = useContext(AuthContext);
  const URL_ARTICULOS_CORTES = process.env.REACT_APP_URL_ARTICULOS_CORTES;

  const hoy = new Date();
  const inicio = new Date();
  inicio.setDate(inicio.getDate() - 30);

  const [selectedFechaInicio, setSelectedFechaInicio] = useState(
    moment(inicio).format("YYYY-MM-DD")
  );
  const [selectedFechaFin, setSelectedFechaFin] = useState(
    moment(hoy).format("YYYY-MM-DD")
  );

  const [renglones, setRenglones] = useState([]);
  const [cargando, setCargando] = useState(false);

  // filtros
  const [selectedCliente, setSelectedCliente] = useState("");
  const [selectedArticulo, setSelectedArticulo] = useState("");
  const [selectedPedido, setSelectedPedido] = useState("");
  const [selectedProceso, setSelectedProceso] = useState("");
  const [selectedProveedor, setSelectedProveedor] = useState("");
  const [selectedTela, setSelectedTela] = useState("");
  const [search, setSearch] = useState("");
  // qué corte tiene el desglose abierto en pantalla
  const [abierto, setAbierto] = useState("");

  const headers = {
    Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
  };

  const arr = (d) => (Array.isArray(d) ? d : []);
  const n = (v) => Number(v || 0) || 0;
  const num = (v, dec = 0) =>
    new Intl.NumberFormat("en-US", {
      minimumFractionDigits: dec,
      maximumFractionDigits: dec,
    }).format(n(v));
  const money = (v) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 2,
    }).format(n(v));
  const mts = (v) => `${num(v, 2)} m`;

  // (2026-10) subcliente y subárea de la orden entre paréntesis (ListadoOrdenesCorte)
  const clienteConSub = (r) => {
    const conDato = (v) => {
      const t = String(v ?? "").trim();
      return t && t.toUpperCase() !== "NA" ? t : "";
    };
    const sub = conDato(r?.subCliente);
    const subArea = conDato(r?.subArea);
    return [String(r?.cliente || "").trim(), sub && `(${sub})`, subArea && `(${subArea})`]
      .filter(Boolean)
      .join(" ");
  };

  const claveRenglon = (r) => `${r.corte}-${r.articuloId || r.articulo}`;

  const fetchCostos = () => {
    setCargando(true);
    axios
      .get(
        `${URL_ARTICULOS_CORTES}CostosFechas/${selectedFechaInicio}/${selectedFechaFin}`,
        { headers }
      )
      .then((r) => {
        setCargando(false);
        setRenglones(
          arr(r.data).sort((a, b) => Number(b.corte || 0) - Number(a.corte || 0))
        );
      })
      .catch((err) => {
        console.log(err);
        setCargando(false);
        const d = err?.response?.data;
        const detalle =
          (typeof d === "string" ? d : d?.error || d?.message) ||
          err?.message ||
          "Error desconocido";
        Swal.fire("Error", `No se pudieron cargar los costos: ${detalle}`, "error");
      });
  };

  useEffect(() => {
    fetchCostos();
    // el reporte tarda: sólo se recalcula con el botón Actualizar
  }, []);

  // ---------- catálogos de los combos, armados con lo que trae el periodo ----------
  const unicos = (lista) =>
    [...new Set(lista.filter((x) => String(x || "").trim()))].sort((a, b) =>
      String(a).localeCompare(String(b), "es", { numeric: true })
    );

  const clientesLista = useMemo(
    () => unicos(renglones.map((r) => r.cliente)),
    [renglones]
  );
  const articulosLista = useMemo(
    () => unicos(renglones.map((r) => r.codigo || r.articulo)),
    [renglones]
  );
  const pedidosLista = useMemo(
    () => unicos(renglones.map((r) => r.pedido)),
    [renglones]
  );
  const procesosLista = useMemo(
    () =>
      unicos(
        renglones.flatMap((r) => (r.detalleProcesos || []).map((p) => p.proceso))
      ),
    [renglones]
  );
  const proveedoresLista = useMemo(
    () =>
      unicos(
        renglones.flatMap((r) => (r.detalleProcesos || []).map((p) => p.proveedor))
      ),
    [renglones]
  );
  const telasLista = useMemo(
    () =>
      unicos(renglones.flatMap((r) => (r.detalleTela || []).map((t) => t.concepto))),
    [renglones]
  );

  // si al cambiar el periodo lo elegido ya no existe, se regresa a "Todos"
  useEffect(() => {
    if (selectedCliente && !clientesLista.includes(selectedCliente)) setSelectedCliente("");
    if (selectedArticulo && !articulosLista.includes(selectedArticulo)) setSelectedArticulo("");
    if (selectedPedido && !pedidosLista.includes(selectedPedido)) setSelectedPedido("");
    if (selectedProceso && !procesosLista.includes(selectedProceso)) setSelectedProceso("");
    if (selectedProveedor && !proveedoresLista.includes(selectedProveedor)) setSelectedProveedor("");
    if (selectedTela && !telasLista.includes(selectedTela)) setSelectedTela("");
  }, [clientesLista, articulosLista, pedidosLista, procesosLista, proveedoresLista, telasLista]);

  const filtrados = useMemo(() => {
    const q = String(search || "").trim().toLowerCase();
    const texto = (r) =>
      [
        r.corte,
        r.fecha,
        r.pedido,
        clienteConSub(r),
        r.codigo,
        r.articulo,
        r.cantidad,
        r.tela,
        r.habilitacion,
        r.procesos,
        r.total,
        r.totalPorPieza,
        ...(r.detalleTela || []).map((t) => `${t.concepto} ${t.color}`),
        ...(r.detalleProcesos || []).map((p) => `${p.proceso} ${p.proveedor}`),
        ...(r.detalleHabilitacion || []).map((h) => h.insumo),
      ]
        .map((v) => String(v ?? ""))
        .join(" ")
        .toLowerCase();

    return renglones.filter(
      (r) =>
        (!selectedCliente || r.cliente === selectedCliente) &&
        (!selectedArticulo || (r.codigo || r.articulo) === selectedArticulo) &&
        (!selectedPedido || String(r.pedido) === String(selectedPedido)) &&
        (!selectedProceso ||
          (r.detalleProcesos || []).some((p) => p.proceso === selectedProceso)) &&
        (!selectedProveedor ||
          (r.detalleProcesos || []).some((p) => p.proveedor === selectedProveedor)) &&
        (!selectedTela ||
          (r.detalleTela || []).some((t) => t.concepto === selectedTela)) &&
        (!q || texto(r).includes(q))
    );
  }, [
    renglones,
    selectedCliente,
    selectedArticulo,
    selectedPedido,
    selectedProceso,
    selectedProveedor,
    selectedTela,
    search,
  ]);

  const tot = useMemo(() => {
    const t = { piezas: 0, tela: 0, procesos: 0, habilitacion: 0, total: 0 };
    filtrados.forEach((r) => {
      t.piezas += n(r.cantidad);
      t.tela += n(r.tela);
      t.procesos += n(r.procesos);
      t.habilitacion += n(r.habilitacion);
      t.total += n(r.total);
    });
    t.porPieza = t.piezas > 0 ? t.total / t.piezas : 0;
    t.cortes = new Set(filtrados.map((r) => r.corte)).size;
    return t;
  }, [filtrados]);

  const limpiarFiltros = () => {
    setSelectedCliente("");
    setSelectedArticulo("");
    setSelectedPedido("");
    setSelectedProceso("");
    setSelectedProveedor("");
    setSelectedTela("");
    setSearch("");
  };

  const estiloKpi = {
    fontSize: "1.6rem",
    fontWeight: 700,
    lineHeight: 1.15,
    margin: "2px 0",
  };

  // ------------------------------- PDF -------------------------------
  // Réplica de la ficha: encabezado, franja de filtros, tabla resumen y, por
  // cada corte, el desglose de telas / procesos / habilitación y su total.
  const PDFReporte = (soloEste = null) => {
    const lista = soloEste ? [soloEste] : filtrados;
    if (!lista.length) {
      return Swal.fire("Sin datos", "No hay cortes con los filtros actuales", "warning");
    }

    const doc = new jsPDF("portrait", "mm", "a4");
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const ML = 14;
    const usableW = pageW - ML * 2;

    const azul = [37, 99, 235];
    const azulClaro = [239, 246, 255];
    const azulMarino = [15, 23, 42];
    const gris = [100, 116, 139];
    const grisClaro = [248, 250, 252];
    const borde = [226, 232, 240];

    let y = 20;

    const espacio = (alto) => {
      if (y + alto > pageH - 18) {
        doc.addPage();
        y = 20;
      }
    };

    // ---- Encabezado
    doc.setFont(undefined, "bold").setFontSize(21).setTextColor(...azulMarino);
    doc.text("Artículos Costos Cortes", ML, y);
    doc.setFont(undefined, "normal").setFontSize(9).setTextColor(...gris);
    doc.text("Sistema ERP — Control de Producción y Costeo General", ML, y + 6);

    doc.setFont(undefined, "bold").setFontSize(9).setTextColor(...azulMarino);
    doc.text(
      `Fecha de Emisión: ${moment().format("DD/MM/YYYY")}`,
      pageW - ML,
      y - 1,
      { align: "right" }
    );
    doc.text(`Registros: ${lista.length}`, pageW - ML, y + 4, { align: "right" });

    y += 10;
    doc.setDrawColor(...azul).setLineWidth(0.8);
    doc.line(ML, y, pageW - ML, y);
    y += 7;

    // ---- Franja de filtros
    const filtrosTxt = [
      selectedCliente ? `Cliente: ${selectedCliente}` : "",
      selectedArticulo ? `Artículo: ${selectedArticulo}` : "",
      selectedPedido ? `Pedido: ${selectedPedido}` : "",
      selectedProceso ? `Proceso: ${selectedProceso}` : "",
      selectedProveedor ? `Taller: ${selectedProveedor}` : "",
      selectedTela ? `Tela: ${selectedTela}` : "",
      search ? `Busca: ${search}` : "",
    ].filter(Boolean);

    doc.setFillColor(...grisClaro);
    doc.setDrawColor(...borde).setLineWidth(0.25);
    const altoFranja = filtrosTxt.length ? 15 : 10;
    doc.roundedRect(ML, y, usableW, altoFranja, 1.5, 1.5, "FD");
    doc.setFont(undefined, "normal").setFontSize(9).setTextColor(...azulMarino);
    doc.text(
      `Fecha Inicio:  ${moment(selectedFechaInicio).format(
        "DD/MM/YYYY"
      )}        Fecha Fin:  ${moment(selectedFechaFin).format("DD/MM/YYYY")}`,
      ML + 4,
      y + 6.5
    );
    doc.text(`Registros mostrados: ${lista.length}`, pageW - ML - 4, y + 6.5, {
      align: "right",
    });
    if (filtrosTxt.length) {
      doc.setFontSize(7.5).setTextColor(...gris);
      doc.text(
        doc.splitTextToSize(filtrosTxt.join("   ·   "), usableW - 8)[0],
        ML + 4,
        y + 12
      );
    }
    y += altoFranja + 6;

    // ---- Tabla resumen
    doc.autoTable({
      startY: y,
      margin: { left: ML, right: ML, top: 20, bottom: 18 },
      tableWidth: usableW,
      theme: "plain",
      head: [[
        "CORTE", "FECHA", "PEDIDO", "CLIENTE", "ARTÍCULO", "CANTIDAD",
        "TELA", "HABILITACIÓN", "PROCESOS", "TOTAL",
      ]],
      body: lista.map((r) => [
        String(r.corte),
        r.fecha,
        String(r.pedido),
        clienteConSub(r),
        String(r.codigo || r.articulo),
        `${num(r.cantidad)} pzas`,
        money(r.tela),
        money(r.habilitacion),
        money(r.procesos),
        money(r.total),
      ]),
      foot: lista.length > 1
        ? [[
            "TOTAL", "", "", "", "",
            `${num(tot.piezas)} pzas`,
            money(tot.tela), money(tot.habilitacion), money(tot.procesos), money(tot.total),
          ]]
        : undefined,
      styles: { fontSize: 7.5, cellPadding: 2, textColor: azulMarino, lineColor: borde, lineWidth: 0 },
      headStyles: {
        fontSize: 7, fontStyle: "bold", textColor: gris, fillColor: grisClaro,
        lineWidth: { bottom: 0.3 }, lineColor: borde,
      },
      footStyles: { fontSize: 7.5, fontStyle: "bold", textColor: azulMarino, fillColor: grisClaro },
      bodyStyles: { lineWidth: { bottom: 0.2 }, lineColor: borde },
      columnStyles: {
        0: { fontStyle: "bold" },
        5: { halign: "right", fontStyle: "bold" },
        6: { halign: "right" },
        7: { halign: "right" },
        8: { halign: "right" },
        9: { halign: "right", fontStyle: "bold" },
      },
    });
    y = doc.autoTable.previous.finalY + 8;

    // ---- Ficha por corte
    const seccion = (titulo, subtotalTxt) => {
      espacio(16);
      doc.setFont(undefined, "bold").setFontSize(10.5).setTextColor(...azulMarino);
      doc.text(titulo, ML, y + 4);
      if (subtotalTxt) {
        doc.setFillColor(...azulClaro);
        const w = doc.getTextWidth(subtotalTxt) + 8;
        doc.roundedRect(pageW - ML - w, y - 1.5, w, 7.5, 1.5, 1.5, "F");
        doc.setFontSize(9).setTextColor(...azul);
        doc.text(subtotalTxt, pageW - ML - 4, y + 3.8, { align: "right" });
      }
      y += 9;
    };

    const tabla = (head, body, vacio, columnStyles) => {
      doc.autoTable({
        startY: y,
        margin: { left: ML, right: ML, top: 20, bottom: 18 },
        tableWidth: usableW,
        theme: "plain",
        head: [head],
        body: body.length ? body : [[{ content: vacio, colSpan: head.length, styles: { halign: "center", textColor: gris, fontStyle: "italic" } }]],
        styles: { fontSize: 7.5, cellPadding: 2, textColor: azulMarino },
        headStyles: {
          fontSize: 6.8, fontStyle: "bold", textColor: gris, fillColor: grisClaro,
          lineWidth: { bottom: 0.3 }, lineColor: borde,
        },
        bodyStyles: { lineWidth: { bottom: 0.2 }, lineColor: borde },
        columnStyles: columnStyles || {},
      });
      y = doc.autoTable.previous.finalY + 6;
    };

    lista.forEach((r) => {
      espacio(60);

      seccion("Desglose de Telas (Consumo por Pieza y Unidad)", `Subtotal Tela: ${money(r.tela)}`);
      tabla(
        ["CONCEPTO / TIPO DE TELA", "COLOR", "CONSUMO / PZA", "CONSUMO TOTAL", "PRECIO X METRO", "COSTO X PZA", "COSTO TOTAL"],
        (r.detalleTela || []).map((t) => [
          t.concepto, t.color || "-", mts(t.consumoPorPieza), mts(t.metros),
          money(t.precio), money(t.costoPorPieza), money(t.costo),
        ]),
        "Sin tela cargada en este corte.",
        { 2: { halign: "right" }, 3: { halign: "right" }, 4: { halign: "right" }, 5: { halign: "right" }, 6: { halign: "right", fontStyle: "bold" } }
      );

      seccion("Desglose de Procesos Operativos", `Subtotal Procesos: ${money(r.procesos)}`);
      tabla(
        ["TIPO DE PROCESO", "PROVEEDOR / TALLER", "CANTIDAD", "PRECIO UNITARIO (X PZA)", "COSTO X PZA", "COSTO TOTAL"],
        (r.detalleProcesos || []).map((p) => [
          p.proceso, p.proveedor || "-", `${num(p.cantidad)} pzas`,
          money(p.precio), money(p.costoPorPieza), money(p.costo),
        ]),
        "Sin procesos cargados en este corte.",
        { 2: { halign: "right" }, 3: { halign: "right" }, 4: { halign: "right" }, 5: { halign: "right", fontStyle: "bold" } }
      );

      seccion("Desglose de Habilitación / Avíos", `Subtotal Habilitación: ${money(r.habilitacion)}`);
      tabla(
        ["INSUMO", "CONSUMO / PZA", "CONSUMO TOTAL", "PRECIO UNITARIO", "COSTO X PZA", "COSTO TOTAL"],
        (r.detalleHabilitacion || []).map((h) => [
          h.insumo, num(h.consumoPorPieza, 2), num(h.cantidad, 2),
          money(h.precio), money(h.costoPorPieza), money(h.costo),
        ]),
        "Sin insumos de habilitación cargados en este corte.",
        { 1: { halign: "right" }, 2: { halign: "right" }, 3: { halign: "right" }, 4: { halign: "right" }, 5: { halign: "right", fontStyle: "bold" } }
      );

      // ---- Banda de total del corte
      espacio(22);
      doc.setFillColor(...azulMarino);
      doc.roundedRect(ML, y, usableW, 17, 2, 2, "F");
      doc.setFont(undefined, "bold").setFontSize(11).setTextColor(255, 255, 255);
      doc.text(
        `COSTO TOTAL DEL CORTE #${r.corte} (${num(r.cantidad)} PIEZAS)`,
        ML + 5,
        y + 7
      );
      doc.setFont(undefined, "normal").setFontSize(8).setTextColor(200, 214, 240);
      doc.text(
        `Artículo: ${r.codigo || r.articulo} | Cliente: ${clienteConSub(r) || "-"}`,
        ML + 5,
        y + 12.5
      );
      doc.setFontSize(7.5).setTextColor(200, 214, 240);
      doc.text("COSTO TOTAL / UNITARIO", pageW - ML - 5, y + 5.5, { align: "right" });
      doc.setFont(undefined, "bold").setFontSize(14).setTextColor(125, 185, 255);
      doc.text(
        `${money(r.total)}  (${money(r.totalPorPieza)} / pza)`,
        pageW - ML - 5,
        y + 12.5,
        { align: "right" }
      );
      doc.setTextColor(0);
      y += 24;
    });

    // ---- Pie
    const paginas = doc.internal.getNumberOfPages();
    for (let p = 1; p <= paginas; p++) {
      doc.setPage(p);
      doc.setDrawColor(...borde).setLineWidth(0.25);
      doc.line(ML, pageH - 14, pageW - ML, pageH - 14);
      doc.setFont(undefined, "normal").setFontSize(8).setTextColor(...gris);
      doc.text("ERP Módulo Costos — Reporte de Ficha de Corte", ML, pageH - 9);
      doc.text(`Página ${p} de ${paginas}`, pageW - ML, pageH - 9, { align: "right" });
    }

    window.open(doc.output("bloburl"), "_blank");
  };

  // ------------------------------ Excel ------------------------------
  const excel = () => {
    if (!filtrados.length) {
      return Swal.fire("Sin datos", "No hay cortes con los filtros actuales", "warning");
    }

    const resumen = filtrados.map((r) => ({
      Corte: r.corte,
      Fecha: r.fecha,
      Pedido: r.pedido,
      Cliente: clienteConSub(r),
      Articulo: r.codigo || r.articulo,
      Descripcion: r.descripcion || "",
      Cantidad: n(r.cantidad),
      Tela: n(r.tela),
      Habilitacion: n(r.habilitacion),
      Procesos: n(r.procesos),
      Total: n(r.total),
      CostoPorPieza: n(r.totalPorPieza),
    }));

    const desglose = [];
    filtrados.forEach((r) => {
      const base = {
        Corte: r.corte,
        Fecha: r.fecha,
        Pedido: r.pedido,
        Cliente: clienteConSub(r),
        Articulo: r.codigo || r.articulo,
        Cantidad: n(r.cantidad),
      };
      (r.detalleTela || []).forEach((t) =>
        desglose.push({
          ...base, Bloque: "Tela", Concepto: t.concepto, Detalle: t.color || "",
          ConsumoPorPieza: n(t.consumoPorPieza), ConsumoTotal: n(t.metros),
          PrecioUnitario: n(t.precio), CostoPorPieza: n(t.costoPorPieza), CostoTotal: n(t.costo),
        })
      );
      (r.detalleProcesos || []).forEach((p) =>
        desglose.push({
          ...base, Bloque: "Proceso", Concepto: p.proceso, Detalle: p.proveedor || "",
          ConsumoPorPieza: "", ConsumoTotal: n(p.cantidad),
          PrecioUnitario: n(p.precio), CostoPorPieza: n(p.costoPorPieza), CostoTotal: n(p.costo),
        })
      );
      (r.detalleHabilitacion || []).forEach((h) =>
        desglose.push({
          ...base, Bloque: "Habilitación", Concepto: h.insumo, Detalle: h.unidad || "",
          ConsumoPorPieza: n(h.consumoPorPieza), ConsumoTotal: n(h.cantidad),
          PrecioUnitario: n(h.precio), CostoPorPieza: n(h.costoPorPieza), CostoTotal: n(h.costo),
        })
      );
    });

    const option = {
      fileName: "ReporteCostosCortes",
      datas: [
        {
          sheetData: resumen,
          sheetName: "Resumen",
          sheetFilter: [
            "Corte", "Fecha", "Pedido", "Cliente", "Articulo", "Descripcion",
            "Cantidad", "Tela", "Habilitacion", "Procesos", "Total", "CostoPorPieza",
          ],
          sheetHeader: [
            "Corte", "Fecha", "Pedido", "Cliente", "Artículo", "Descripción",
            "Cantidad", "Tela", "Habilitación", "Procesos", "Total", "Costo / Pza",
          ],
        },
        {
          sheetData: desglose,
          sheetName: "Desglose",
          sheetFilter: [
            "Corte", "Fecha", "Pedido", "Cliente", "Articulo", "Cantidad", "Bloque",
            "Concepto", "Detalle", "ConsumoPorPieza", "ConsumoTotal",
            "PrecioUnitario", "CostoPorPieza", "CostoTotal",
          ],
          sheetHeader: [
            "Corte", "Fecha", "Pedido", "Cliente", "Artículo", "Cantidad", "Bloque",
            "Concepto", "Color / Taller / Unidad", "Consumo / Pza", "Consumo Total",
            "Precio Unitario", "Costo / Pza", "Costo Total",
          ],
        },
      ],
    };

    new ExportJsonExcel(option).saveExcel();
  };

  const comboSimple = (label, valor, setValor, lista, ancho = 2) => (
    <Col md={ancho}>
      <Label>{label}</Label>
      <Input
        bsSize="sm"
        type="select"
        value={valor}
        onChange={(e) => setValor(e.target.value)}
      >
        <option value="">Todos</option>
        {lista.map((x) => (
          <option key={x} value={x}>
            {x}
          </option>
        ))}
      </Input>
    </Col>
  );

  return (
    <>
      <Header />
      <br />
      <br />
      {user.menu_produccion || user.menu_compras ? (
        <div className="card col-12">
          <Row>
            <Col md={8}>
              <Button
                size="sm"
                href="/MenuProduccion"
                className="btn btn-danger"
                id="botonListado"
              >
                Regresar
              </Button>
            </Col>
            <Col sm={4}>
              <ButtonGroup id="logoutBoton">
                <Button size="sm" className="btn" color="danger" onClick={() => PDFReporte()}>
                  PDF <i className="far fa-file-pdf"></i>
                </Button>
                <Button size="sm" className="btn" color="success" onClick={excel}>
                  Excel <i className="far fa-file-excel"></i>
                </Button>
              </ButtonGroup>
            </Col>
          </Row>

          <h3 align="center">Artículos Costos Cortes</h3>

          <Row>
            <Col md={2}>
              <Label>Fecha Inicio</Label>
              <Input
                bsSize="sm"
                type="date"
                value={selectedFechaInicio}
                onChange={(e) => setSelectedFechaInicio(e.target.value)}
              />
            </Col>
            <Col md={2}>
              <Label>Fecha Fin</Label>
              <Input
                bsSize="sm"
                type="date"
                value={selectedFechaFin}
                onChange={(e) => setSelectedFechaFin(e.target.value)}
              />
            </Col>
            <Col md={2} className="d-flex align-items-end">
              <Button size="sm" color="info" disabled={cargando} onClick={fetchCostos}>
                {cargando ? "Calculando..." : "Actualizar Fechas"}
              </Button>
            </Col>
            {comboSimple("Cliente", selectedCliente, setSelectedCliente, clientesLista, 3)}
            {comboSimple("Artículo", selectedArticulo, setSelectedArticulo, articulosLista)}
          </Row>

          <Row className="mt-2">
            {comboSimple("Pedido", selectedPedido, setSelectedPedido, pedidosLista)}
            {comboSimple("Proceso", selectedProceso, setSelectedProceso, procesosLista)}
            {comboSimple("Taller / Proveedor", selectedProveedor, setSelectedProveedor, proveedoresLista, 3)}
            {comboSimple("Tela", selectedTela, setSelectedTela, telasLista, 2)}
            <Col md={2}>
              <Label>Buscar</Label>
              <Input
                bsSize="sm"
                type="text"
                placeholder="Corte, cliente, tela, taller..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </Col>
            <Col md={1} className="d-flex align-items-end">
              <Button size="sm" color="secondary" onClick={limpiarFiltros}>
                Limpiar
              </Button>
            </Col>
          </Row>

          <br />

          <Row>
            <Col md>
              <div className="card p-2 text-center">
                <small className="text-muted">CORTES</small>
                <div style={estiloKpi}>{num(tot.cortes)}</div>
                <small className="text-muted">{num(filtrados.length)} renglones</small>
              </div>
            </Col>
            <Col md>
              <div className="card p-2 text-center">
                <small className="text-muted">PIEZAS</small>
                <div style={estiloKpi}>{num(tot.piezas)}</div>
                <small className="text-muted">cortadas</small>
              </div>
            </Col>
            <Col md>
              <div className="card p-2 text-center">
                <small className="text-muted">TELA</small>
                <div style={estiloKpi}>{money(tot.tela)}</div>
                <small className="text-muted">
                  {tot.total > 0 ? ((tot.tela / tot.total) * 100).toFixed(1) : 0}% del costo
                </small>
              </div>
            </Col>
            <Col md>
              <div className="card p-2 text-center">
                <small className="text-muted">PROCESOS</small>
                <div style={estiloKpi}>{money(tot.procesos)}</div>
                <small className="text-muted">
                  {tot.total > 0 ? ((tot.procesos / tot.total) * 100).toFixed(1) : 0}% del costo
                </small>
              </div>
            </Col>
            <Col md>
              <div className="card p-2 text-center">
                <small className="text-muted">HABILITACIÓN</small>
                <div style={estiloKpi}>{money(tot.habilitacion)}</div>
                <small className="text-muted">
                  {tot.total > 0 ? ((tot.habilitacion / tot.total) * 100).toFixed(1) : 0}% del costo
                </small>
              </div>
            </Col>
            <Col md>
              <div
                className="card p-2 text-center"
                style={{ borderTop: "3px solid #2563EB" }}
              >
                <small className="text-muted">COSTO TOTAL</small>
                <div style={{ ...estiloKpi, color: "#2563EB" }}>{money(tot.total)}</div>
                <small className="text-muted">{money(tot.porPieza)} / pza</small>
              </div>
            </Col>
          </Row>

          <br />
          {cargando ? (
            <h5 align="center" className="text-muted">
              Calculando costos, puede tardar unos segundos...
            </h5>
          ) : null}

          <Table size="sm" striped hover className="table-responsive-xl">
            <thead>
              <tr>
                <th>Corte</th>
                <th>Fecha</th>
                <th>Pedido</th>
                <th>Cliente</th>
                <th>Artículo</th>
                <th className="text-end">Cantidad</th>
                <th className="text-end">Tela</th>
                <th className="text-end">Habilitación</th>
                <th className="text-end">Procesos</th>
                <th className="text-end">Total</th>
                <th className="text-end">Costo / Pza</th>
                <th className="text-center">Ficha</th>
              </tr>
            </thead>
            <tbody>
              {filtrados.map((r) => {
                const key = claveRenglon(r);
                const expandido = abierto === key;
                return (
                  <React.Fragment key={key}>
                    <tr>
                      <td className="negrita">{r.corte}</td>
                      <td>{r.fecha}</td>
                      <td>{r.pedido}</td>
                      <td>{clienteConSub(r)}</td>
                      <td title={r.descripcion || ""}>{r.codigo || r.articulo}</td>
                      <td className="text-end">{num(r.cantidad)}</td>
                      <td className="text-end">{money(r.tela)}</td>
                      <td className="text-end">{money(r.habilitacion)}</td>
                      <td className="text-end">{money(r.procesos)}</td>
                      <td className="text-end negrita">{money(r.total)}</td>
                      <td className="text-end">{money(r.totalPorPieza)}</td>
                      <td className="text-center">
                        <Button
                          size="sm"
                          color={expandido ? "secondary" : "info"}
                          onClick={() => setAbierto(expandido ? "" : key)}
                          title="Ver desglose"
                        >
                          <i className={expandido ? "fas fa-chevron-up" : "fas fa-list"}></i>
                        </Button>{" "}
                        <Button
                          size="sm"
                          color="danger"
                          onClick={() => PDFReporte(r)}
                          title="PDF de este corte"
                        >
                          <i className="far fa-file-pdf"></i>
                        </Button>
                      </td>
                    </tr>

                    {expandido && (
                      <tr>
                        <td colSpan="12" style={{ background: "#f8fafc" }}>
                          <div className="p-2">
                            <b>Desglose de Telas</b>{" "}
                            <Badge color="primary">Subtotal {money(r.tela)}</Badge>
                            <Table size="sm" bordered className="mt-1 mb-3 bg-white">
                              <thead>
                                <tr>
                                  <th>Concepto / Tipo de Tela</th>
                                  <th>Color</th>
                                  <th className="text-end">Consumo / Pza</th>
                                  <th className="text-end">Consumo Total</th>
                                  <th className="text-end">Precio x Metro</th>
                                  <th className="text-end">Costo x Pza</th>
                                  <th className="text-end">Costo Total</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(r.detalleTela || []).length ? (
                                  r.detalleTela.map((t, i) => (
                                    <tr key={i}>
                                      <td>{t.concepto}</td>
                                      <td>{t.color || "-"}</td>
                                      <td className="text-end">{mts(t.consumoPorPieza)}</td>
                                      <td className="text-end">{mts(t.metros)}</td>
                                      <td className="text-end">{money(t.precio)}</td>
                                      <td className="text-end">{money(t.costoPorPieza)}</td>
                                      <td className="text-end negrita">{money(t.costo)}</td>
                                    </tr>
                                  ))
                                ) : (
                                  <tr>
                                    <td colSpan="7" className="text-center text-muted">
                                      Sin tela cargada en este corte.
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </Table>

                            <b>Desglose de Procesos Operativos</b>{" "}
                            <Badge color="primary">Subtotal {money(r.procesos)}</Badge>
                            <Table size="sm" bordered className="mt-1 mb-3 bg-white">
                              <thead>
                                <tr>
                                  <th>Tipo de Proceso</th>
                                  <th>Proveedor / Taller</th>
                                  <th className="text-end">Cantidad</th>
                                  <th className="text-end">Precio Unitario</th>
                                  <th className="text-end">Costo x Pza</th>
                                  <th className="text-end">Costo Total</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(r.detalleProcesos || []).length ? (
                                  r.detalleProcesos.map((p, i) => (
                                    <tr key={i}>
                                      <td>{p.proceso}</td>
                                      <td>{p.proveedor || "-"}</td>
                                      <td className="text-end">{num(p.cantidad)}</td>
                                      <td className="text-end">{money(p.precio)}</td>
                                      <td className="text-end">{money(p.costoPorPieza)}</td>
                                      <td className="text-end negrita">{money(p.costo)}</td>
                                    </tr>
                                  ))
                                ) : (
                                  <tr>
                                    <td colSpan="6" className="text-center text-muted">
                                      Sin procesos cargados en este corte.
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </Table>

                            <b>Desglose de Habilitación / Avíos</b>{" "}
                            <Badge color="primary">Subtotal {money(r.habilitacion)}</Badge>
                            <Table size="sm" bordered className="mt-1 mb-0 bg-white">
                              <thead>
                                <tr>
                                  <th>Insumo</th>
                                  <th className="text-end">Consumo / Pza</th>
                                  <th className="text-end">Consumo Total</th>
                                  <th className="text-end">Precio Unitario</th>
                                  <th className="text-end">Costo x Pza</th>
                                  <th className="text-end">Costo Total</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(r.detalleHabilitacion || []).length ? (
                                  r.detalleHabilitacion.map((h, i) => (
                                    <tr key={i}>
                                      <td>{h.insumo}</td>
                                      <td className="text-end">{num(h.consumoPorPieza, 2)}</td>
                                      <td className="text-end">{num(h.cantidad, 2)}</td>
                                      <td className="text-end">{money(h.precio)}</td>
                                      <td className="text-end">{money(h.costoPorPieza)}</td>
                                      <td className="text-end negrita">{money(h.costo)}</td>
                                    </tr>
                                  ))
                                ) : (
                                  <tr>
                                    <td colSpan="6" className="text-center text-muted">
                                      Sin insumos de habilitación cargados en este corte.
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </Table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}

              {!filtrados.length && !cargando ? (
                <tr>
                  <td colSpan="12" className="text-center text-muted">
                    Sin cortes en el periodo / filtros seleccionados.
                  </td>
                </tr>
              ) : null}
            </tbody>
            {filtrados.length ? (
              <tfoot>
                <tr style={{ background: "#f1f3f5" }}>
                  <th colSpan="5" className="text-end">
                    TOTALES
                  </th>
                  <th className="text-end">{num(tot.piezas)}</th>
                  <th className="text-end">{money(tot.tela)}</th>
                  <th className="text-end">{money(tot.habilitacion)}</th>
                  <th className="text-end">{money(tot.procesos)}</th>
                  <th className="text-end">{money(tot.total)}</th>
                  <th className="text-end">{money(tot.porPieza)}</th>
                  <th></th>
                </tr>
              </tfoot>
            ) : null}
          </Table>
        </div>
      ) : undefined}
      <br />
    </>
  );
}

export default ReporteCostosCortes;
